import React, { useState } from 'react';
import { useApi } from '../../contexts/ApiContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import UserSearch from './UserSearch';
import './ShareDocumentDialog.css';

interface User {
  userId: string;
  email: string;
  name?: string;
}

interface Permission {
  canView: boolean;
  canDownload: boolean;
  canPrint: boolean;
  watermark: boolean;
  watermarkText?: string;
  allowedPages?: number[];
  deniedPages?: number[];
  expiresAt?: string;
}

interface ShareDocumentDialogProps {
  documentId: string;
  documentName: string;
  isOpen: boolean;
  onClose: () => void;
  onShareSuccess?: (viewerId: string, permissions: Permission) => void;
}

const ShareDocumentDialog: React.FC<ShareDocumentDialogProps> = ({
  documentId,
  documentName,
  isOpen,
  onClose,
  onShareSuccess
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Permission>({
    canView: true,
    canDownload: false,
    canPrint: false,
    watermark: false
  });
  const [pageRestrictions, setPageRestrictions] = useState<'none' | 'allowed' | 'denied'>('none');
  const [pageNumbers, setPageNumbers] = useState('');
  const [enableExpiry, setEnableExpiry] = useState(false);
  const [expiryDateTime, setExpiryDateTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { permissions: permissionsApi } = useApi();

  const handlePermissionChange = (permission: keyof Permission, value: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [permission]: value,
      ...(permission === 'watermark' && !value ? { watermarkText: '' } : {}),
    }));
  };

  const parsePageNumbers = (input: string): number[] => {
    if (!input.trim()) return [];
    const pages: number[] = [];
    for (const part of input.split(',')) {
      const trimmed = part.trim();
      if (trimmed.includes('-')) {
        const [start, end] = trimmed.split('-').map(n => parseInt(n.trim()));
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) pages.push(i);
        }
      } else {
        const pageNum = parseInt(trimmed);
        if (!isNaN(pageNum)) pages.push(pageNum);
      }
    }
    return Array.from(new Set(pages)).sort((a, b) => a - b);
  };

  const buildSharePermissions = (): Permission => {
    const sharePermissions: Permission = { ...permissions };
    if (pageRestrictions !== 'none' && pageNumbers.trim()) {
      const pages = parsePageNumbers(pageNumbers);
      if (pages.length > 0) {
        if (pageRestrictions === 'allowed') sharePermissions.allowedPages = pages;
        else sharePermissions.deniedPages = pages;
      }
    }
    if (enableExpiry && expiryDateTime) {
      sharePermissions.expiresAt = new Date(expiryDateTime).toISOString();
    }
    return sharePermissions;
  };

  const handlePreview = () => {
    const p = buildSharePermissions();
    const params = new URLSearchParams({
      canView: String(p.canView),
      canDownload: String(p.canDownload),
      canPrint: String(p.canPrint),
      watermark: String(p.watermark),
      ...(p.allowedPages ? { allowedPages: p.allowedPages.join(',') } : {}),
      ...(p.deniedPages ? { deniedPages: p.deniedPages.join(',') } : {}),
    });
    window.open(`/documents/${documentId}/preview?${params}`, '_blank');
  };

  const handleShare = async () => {
    if (!selectedUser) {
      setError('Please enter an email address to share with');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const sharePermissions = buildSharePermissions();
      await permissionsApi.shareDocument(documentId, selectedUser.userId, sharePermissions);
      if (onShareSuccess) onShareSuccess(selectedUser.userId, sharePermissions);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to share document');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setPermissions({ canView: true, canDownload: false, canPrint: false, watermark: false, watermarkText: '' });
    setPageRestrictions('none');
    setPageNumbers('');
    setEnableExpiry(false);
    setExpiryDateTime('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Share Document</h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close">×</button>
          </div>

          <div className="modal-body">
            {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}

            <div className="document-info mb-3">
              <h6>📄 {documentName}</h6>
              <small className="text-muted">Configure permissions for the recipient</small>
            </div>

            <div className="form-group mb-3">
              <label className="form-label">Share with Email Address</label>
              <UserSearch
                onUserSelect={setSelectedUser}
                selectedUser={selectedUser}
                placeholder="Enter email address..."
              />
            </div>

            <div className="form-group mb-3">
              <label className="form-label">Permissions</label>
              <div className="permission-checkboxes">
                {([
                  { key: 'canView', label: 'View', desc: 'Can view the document pages' },
                  { key: 'canDownload', label: 'Download', desc: 'Can download the document' },
                  { key: 'canPrint', label: 'Print', desc: 'Can print the document' },
                  { key: 'watermark', label: 'Watermark', desc: 'Apply watermark to viewed/downloaded pages' },
                ] as const).map(({ key, label, desc }) => (
                  <div className="form-check" key={key}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={key}
                      checked={permissions[key]}
                      onChange={(e) => handlePermissionChange(key, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={key}>
                      <strong>{label}</strong> - {desc}
                    </label>
                  </div>
                ))}
              </div>
              {permissions.watermark && (
                <div className="mt-2">
                  <label className="form-label" style={{ fontSize: '0.875rem' }}>Watermark Text (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Default: CONFIDENTIAL  <viewer email>"
                    value={permissions.watermarkText || ''}
                    onChange={(e) => setPermissions(prev => ({ ...prev, watermarkText: e.target.value }))}
                  />
                  <small className="form-text text-muted">Leave blank to use the default watermark text.</small>
                </div>
              )}
            </div>

            <div className="form-group mb-3">
              <label className="form-label">Page Restrictions (Optional)</label>
              <div className="page-restrictions">
                {([
                  { value: 'none', label: 'No restrictions - Access to all pages' },
                  { value: 'allowed', label: 'Allow specific pages only' },
                  { value: 'denied', label: 'Deny specific pages' },
                ] as const).map(({ value, label }) => (
                  <div className="form-check" key={value}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="pageRestrictions"
                      id={value}
                      checked={pageRestrictions === value}
                      onChange={() => setPageRestrictions(value)}
                    />
                    <label className="form-check-label" htmlFor={value}>{label}</label>
                  </div>
                ))}
              </div>
              {pageRestrictions !== 'none' && (
                <div className="page-numbers-input mt-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., 1,3,5-10,15"
                    value={pageNumbers}
                    onChange={(e) => setPageNumbers(e.target.value)}
                  />
                  <small className="form-text text-muted">
                    Enter page numbers separated by commas. Use ranges like "5-10" for multiple pages.
                  </small>
                </div>
              )}
            </div>

            <div className="form-group mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="enableExpiry"
                  checked={enableExpiry}
                  onChange={(e) => {
                    setEnableExpiry(e.target.checked);
                    if (!e.target.checked) setExpiryDateTime('');
                  }}
                />
                <label className="form-check-label" htmlFor="enableExpiry">
                  <strong>Set link expiration</strong> - Link stops working after this date and time
                </label>
              </div>
              {enableExpiry && (
                <div className="mt-2">
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={expiryDateTime}
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    onChange={(e) => setExpiryDateTime(e.target.value)}
                  />
                  <small className="form-text text-muted">
                    After this time the recipient will no longer be able to access the document.
                  </small>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={handlePreview} disabled={loading}>
              Preview
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleShare}
              disabled={loading || !selectedUser}
            >
              {loading ? <><LoadingSpinner size="small" inline />Sharing...</> : 'Share Document'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareDocumentDialog;
